/**
 * tour.json — the core runtime artifact of RoomTour (codename Pisorama).
 *
 * This is the contract between the processing pipeline (functions, which WRITES it)
 * and the embeddable viewer (apps/viewer, which READS it). It is intentionally
 * VIEWER-AGNOSTIC: it describes a multi-node panorama tour + a floor-plan minimap in
 * neutral terms. apps/viewer/src/lib/adaptTour.ts maps it onto Photo Sphere Viewer's
 * VirtualTourPlugin + PlanPlugin shapes. Swapping viewer libraries must not require
 * changing this schema or anything that produced it.
 *
 * Coordinates & units:
 *  - All world positions are in METRES, in the floor-plan plane (x = east, y = north),
 *    origin at the floor-plan image's top-left mapped via `plan.originMeters`.
 *  - Yaw is RADIANS, 0 = +x (east), increasing counter-clockwise (three.js convention).
 *  - These positions come from ARKit world tracking, so they carry ~10–20cm drift.
 *    Treat them as approximate. Never surface them as survey-grade measurements.
 */
import { z } from 'zod';
import { AREA_UNITS } from './locale.js';
/** Schema version. Bump on any breaking change; the viewer branches on this. */
export const TOUR_SCHEMA_VERSION = 1;
/** A 2D point in metres in the floor-plan plane. */
export const Vec2 = z.object({ x: z.number().finite(), y: z.number().finite() });
/** Optional measured dimensions; old tours contain only floor-plane endpoints. */
export const PlanWall = z.object({
    a: Vec2,
    b: Vec2,
    height: z.number().finite().positive().optional(),
    thickness: z.number().finite().positive().optional(),
});
/**
 * A door or window, as the span it occupies along a wall (both ends in metres). Doors
 * become gaps in the model's walls; windows are drawn on them. Read from an uploaded
 * plano (see planReading.ts), or from per-room LiDAR scans (since 2026-10-28).
 */
export const PlanOpening = z.object({
    kind: z.enum(['door', 'window']),
    a: Vec2,
    b: Vec2,
});
/**
 * A named room outline (metres): read from a plano, or a LiDAR room's floor; the model
 * lights it when selected.
 */
export const PlanRoom = z.object({
    name: z.string().max(60).default(''),
    outline: z.array(Vec2).min(3).max(64),
    /** Floor area, m² (LiDAR rooms only: measured, still approximate). */
    areaSqm: z.number().finite().positive().optional(),
});
/** A self-hosted official SuperSplat page, produced by tools/splat/prepare.mjs. */
export const Splat = z.object({
    viewer: z.string().max(512).regex(/^(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]+\.html$/, 'Expected a tour-relative HTML path without query strings or parent segments'),
});
/**
 * A single hotspot: a clickable link FROM one node TO another, or an info marker.
 * `position` is the on-sphere direction the marker is placed at (yaw/pitch radians).
 */
export const Hotspot = z.object({
    /** Stable id, unique within the node. */
    id: z.string().min(1),
    kind: z.enum(['link', 'info']),
    /** For kind:'link' — the target node id. Omitted for info markers. */
    targetNodeId: z.string().min(1).optional(),
    /** Direction on the sphere where the marker sits. */
    yaw: z.number(),
    pitch: z.number().default(0),
    /** Optional human label (e.g. "Cocina", "Salir al balcón"). */
    label: z.string().optional(),
});
/**
 * One panorama node = one room/standpoint. Maps to a PSV VirtualTour node.
 * `panorama` is a path RELATIVE to the tour's base href (e.g. "panos/n3.jpg"),
 * so a tour folder is fully self-contained and relocatable across CDNs/buckets.
 */
export const TourNode = z.object({
    id: z.string().min(1),
    /** Display name shown in UI / gallery (e.g. "Salón", "Dormitorio 1"). */
    name: z.string().default(''),
    /** Relative path to the equirectangular pano (2:1 JPEG/WebP). */
    panorama: z.string().min(1),
    /** Optional low-res preview/blur-up shown while the full pano streams in. */
    thumbnail: z.string().optional(),
    /**
     * Optional lower-resolution twin of `panorama` (4096×2048 when `panorama` is
     * 8192×4096), same relative-path rules. Viewers show it first, and keep it on devices
     * that can't hold the full texture.
     */
    panoramaLow: z.string().min(1).optional(),
    /**
     * Optional angular radius (RADIANS, measured from straight down, soft edge included)
     * of the synthetic nadir patch in the pano — the disc that hides the hole under the
     * camera and the photographer's feet. Viewers keep the view above it. Absent = unknown
     * (older tours): viewers fall back to a conservative constant.
     */
    nadirPatchRadius: z.number().finite().min(0).max(Math.PI / 2).optional(),
    /** Marker position on the floor-plan minimap (metres). */
    plan: Vec2,
    /** Camera yaw the viewer should face when this node first opens (radians). */
    defaultYaw: z.number().default(0),
    /** Links + info markers anchored in this pano. */
    hotspots: z.array(Hotspot).default([]),
});
/**
 * The floor-plan minimap descriptor. The image itself is rendered by the pipeline
 * from RoomPlan's CapturedRoom wall geometry (see functions/src/pipeline/floorplan.ts),
 * NOT exported directly by RoomPlan — RoomPlan only gives USDZ + parametric surfaces.
 */
export const FloorPlan = z.object({
    /** Relative path to the rendered plan raster (PNG, transparent background). */
    image: z.string().min(1),
    /** Pixel dimensions of `image`. */
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    /**
     * Metres-per-pixel scale, so node plan-coords (metres) can be placed on the image.
     * pixel = (meters - originMeters) / metersPerPixel  (y flipped: image y grows down).
     */
    metersPerPixel: z.number().positive(),
    /** World-metre coordinate that maps to the image's top-left pixel (0,0). */
    originMeters: Vec2,
    /** Optional vector polylines (wall segments) for crisp re-rendering / measure tool. */
    walls: z.array(PlanWall).default([]),
    /** Doors and windows on those walls (uploaded plans and LiDAR scans). */
    openings: z.array(PlanOpening).default([]),
    /** Room outlines (uploaded plans and LiDAR scans). */
    rooms: z.array(PlanRoom).default([]),
    /**
     * Heading of the capture on this plan, radians: the direction of capture-world +X
     * (a panorama's yaw 0) measured in the plan's x/y frame, counter-clockwise from +x
     * toward +y. 0 for RoomPlan plans (drawn in capture coordinates); the pin fit's
     * rotation for uploaded plans. Absent = unknown, and then nothing may project a
     * panorama onto the plan (the Paseo mode stays off).
     */
    panoYaw: z.number().finite().optional(),
    /** Total floor area, m²: the sum of the measured rooms (LiDAR plans only). */
    areaSqm: z.number().finite().positive().optional(),
    /** The owner's unit for showing the plan's measurements. Stored values stay metric. */
    areaUnit: z.enum(AREA_UNITS).optional(),
});
/** Branding shown in the viewer chrome (free tier forces watermark on). */
export const TourBranding = z.object({
    /** Agency display name, e.g. "Inmobiliaria Costa Sol". */
    label: z.string().default('RoomTour'),
    /** Relative or absolute logo URL. */
    logoUrl: z.string().optional(),
    /** Accent colour (hex) for hotspots/UI. */
    accent: z.string().default('#1f6feb'),
    /** If true, render the "Tour por RoomTour" watermark (free tier). */
    watermark: z.boolean().default(true),
});
/** The complete tour document the viewer loads. */
export const Tour = z.object({
    schema: z.literal(TOUR_SCHEMA_VERSION),
    id: z.string().min(1),
    /** Free-text title shown to viewers (e.g. "Piso 3 dormitorios — Marbella"). */
    title: z.string().default(''),
    /** ISO-8601 build timestamp. */
    builtAt: z.string(),
    /** Locale for UI strings; Spain-first. */
    locale: z.enum(['es', 'en', 'ca']).default('es'),
    nodes: z.array(TourNode).min(1),
    /** Node id the tour opens on. */
    startNodeId: z.string().min(1),
    floorPlan: FloorPlan.optional(),
    splat: Splat.optional(),
    branding: TourBranding.default({}),
});
/** Parse + validate an unknown payload into a Tour, throwing on invalid input. */
export function parseTour(input) {
    return Tour.parse(input);
}
/** Non-throwing variant for the viewer (returns a typed result). */
export function safeParseTour(input) {
    return Tour.safeParse(input);
}
//# sourceMappingURL=tour.js.map